import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { productsApi } from '../api/products.api';
import { categoriesApi } from '../api/categories.api';
import { suppliersApi } from '../api/suppliers.api';
import { unitsApi } from '../api/units.api';
import { Product, Category, Supplier, Unit } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PaginatedTable from '../components/common/PaginatedTable';

const emptyProduct: Partial<Product> = {
  code: '',
  name: '',
  categoryId: 0,
  unitId: 0,
  currentStock: 0,
  minStock: 0,
  referencePrice: 0,
  supplierId: 0,
};

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Partial<Product>>(emptyProduct);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);

  const loadProducts = useCallback(async () => {
    try {
      const params: any = {};
      if (search) params.search = search;
      if (filterCategory) params.categoryId = filterCategory;
      const res = await productsApi.getAll(params);
      setProducts(res.data);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  }, [search, filterCategory]);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [catRes, supRes, unitRes] = await Promise.all([
          categoriesApi.getAll(),
          suppliersApi.getAll(),
          unitsApi.getAll(),
        ]);
        setCategories(catRes.data);
        setSuppliers(supRes.data);
        setUnits(unitRes.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    loadAll();
  }, []);

  useEffect(() => {
    if (!loading) loadProducts();
  }, [loading, loadProducts]);

  const handleOpenCreate = () => {
    setSelectedProduct(emptyProduct);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (product: Product) => {
    setSelectedProduct({ ...product });
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEditing && selectedProduct.id) {
        await productsApi.update(selectedProduct.id, selectedProduct);
      } else {
        await productsApi.create(selectedProduct);
      }
      setModalOpen(false);
      loadProducts();
    } catch (err) {
      console.error('Error saving product:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await productsApi.delete(deleteId);
      setDeleteDialogOpen(false);
      loadProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const handleChange = (field: string, value: any) => {
    setSelectedProduct((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Productos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nuevo Producto
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={filterCategory}
            label="Categoria"
            onChange={(e: SelectChangeEvent<number>) => setFilterCategory(e.target.value as number)}
          >
            <MenuItem value={0}>Todas</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <PaginatedTable
        columns={[
          { id: 'code', label: 'Codigo', format: (v: any) => <Chip label={v || '-'} size="small" variant="outlined" /> },
          { id: 'name', label: 'Nombre', format: (v: any) => <span style={{ fontWeight: 500 }}>{v}</span> },
          { id: 'category', label: 'Categoria', format: (_: any, row: any) => row.category?.name || '-' },
          { id: 'unit', label: 'Unidad', format: (_: any, row: any) => row.unitRelation?.abbreviation || row.unit || '-' },
          { id: 'currentStock', label: 'Stock Actual', align: 'right', format: (v: any, row: any) =>
            v < row.minStock ? <Chip label={v} size="small" color="error" /> : v
          },
          { id: 'minStock', label: 'Stock Mínimo', align: 'right' },
          { id: 'referencePrice', label: 'Precio Ref.', align: 'right', format: (v: any) => `S/ ${Number(v || 0).toFixed(2)}` },
          { id: 'supplier', label: 'Proveedor', format: (_: any, row: any) => row.supplier?.name || '-' },
        ]}
        rows={products}
        exportFileName="productos"
        searchFields={['code', 'name']}
        emptyMessage="No hay productos registrados"
        renderActions={(p) => (
          <>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(p)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => { setDeleteId(p.id); setDeleteDialogOpen(true); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Codigo"
                value={selectedProduct.code || ''}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="Auto-generado"
                helperText={!isEditing && !selectedProduct.code ? 'Se genera automaticamente' : ''}
              />
            </Grid>
            <Grid size={8}>
              <TextField
                fullWidth
                label="Nombre"
                value={selectedProduct.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={selectedProduct.categoryId || 0}
                  label="Categoria"
                  onChange={(e) => handleChange('categoryId', e.target.value)}
                >
                  <MenuItem value={0} disabled>Seleccionar</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Unidad de Medida</InputLabel>
                <Select
                  value={selectedProduct.unitId || 0}
                  label="Unidad de Medida"
                  onChange={(e) => handleChange('unitId', e.target.value)}
                >
                  <MenuItem value={0} disabled>Seleccionar</MenuItem>
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Stock Actual"
                type="number"
                value={selectedProduct.currentStock || 0}
                onChange={(e) => handleChange('currentStock', Number(e.target.value))}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Stock Minimo"
                type="number"
                value={selectedProduct.minStock || 0}
                onChange={(e) => handleChange('minStock', Number(e.target.value))}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Precio Ref."
                type="number"
                value={selectedProduct.referencePrice || 0}
                onChange={(e) => handleChange('referencePrice', Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">S/</InputAdornment>,
                }}
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Proveedor</InputLabel>
                <Select
                  value={selectedProduct.supplierId || 0}
                  label="Proveedor"
                  onChange={(e) => handleChange('supplierId', e.target.value)}
                >
                  <MenuItem value={0} disabled>Seleccionar</MenuItem>
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Producto"
        message="¿Estas seguro de que deseas eliminar este producto? Esta accion no se puede deshacer."
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default Products;
